import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Loader2, Globe, ChevronLeft, ChevronRight } from "lucide-react";
import PageHead from "@/components/PageHead";
import JobCard from "@/components/JobCard";
import { api } from "@/lib/api";
import { toast } from "sonner";

const FILTERS = ["All", "Remote", "Hybrid", "Onsite"];

const COUNTRIES = [
  { code: "all", name: "All Countries / Global", flag: "🌐" },
  { code: "us", name: "United States", flag: "🇺🇸" },
  { code: "gb", name: "United Kingdom", flag: "🇬🇧" },
  { code: "ca", name: "Canada", flag: "🇨🇦" },
  { code: "in", name: "India", flag: "🇮🇳" },
  { code: "de", name: "Germany", flag: "🇩🇪" },
  { code: "fr", name: "France", flag: "🇫🇷" },
  { code: "au", name: "Australia", flag: "🇦🇺" },
  { code: "ae", name: "United Arab Emirates", flag: "🇦🇪" },
  { code: "sg", name: "Singapore", flag: "🇸🇬" },
  { code: "nl", name: "Netherlands", flag: "🇳🇱" },
  { code: "ie", name: "Ireland", flag: "🇮🇪" },
  { code: "ch", name: "Switzerland", flag: "🇨🇭" },
  { code: "es", name: "Spain", flag: "🇪🇸" },
  { code: "it", name: "Italy", flag: "🇮🇹" },
  { code: "br", name: "Brazil", flag: "🇧🇷" },
  { code: "za", name: "South Africa", flag: "🇿🇦" },
  { code: "jp", name: "Japan", flag: "🇯🇵" },
];

export default function FindJobs() {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [country, setCountry] = useState("all");
  const [filter, setFilter] = useState("All");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalJobs, setTotalJobs] = useState(0);
  const [saved, setSaved] = useState({});
  const [jobList, setJobList] = useState([]);
  const [loading, setLoading] = useState(true);

  // Reset to page 1 whenever filters change
  const handleQueryChange = (val) => {
    setQ(val);
    setPage(1);
  };

  const handleCountryChange = (val) => {
    setCountry(val);
    setPage(1);
  };

  const handleFilterChange = (val) => {
    setFilter(val);
    setPage(1);
  };

  useEffect(() => {
    let active = true;
    const fetchLiveJobs = async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/jobs", {
          params: { q, work_model: filter, country, page, limit: 12 },
        });
        if (active) {
          setJobList(data?.jobs || []);
          setTotalJobs(data?.total || 0);
          setTotalPages(data?.total_pages || 1);
        }
      } catch (err) {
        if (active) {
          setJobList([]);
          setTotalJobs(0);
          setTotalPages(1);
        }
      } finally {
        if (active) setLoading(false);
      }
    };

    const timer = setTimeout(() => {
      fetchLiveJobs();
    }, 300);

    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [q, filter, country, page]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages && newPage !== page) {
      setPage(newPage);
      window.scrollTo({ top: 120, behavior: "smooth" });
    }
  };

  const startIdx = totalJobs === 0 ? 0 : (page - 1) * 12 + 1;
  const endIdx = Math.min(page * 12, totalJobs);

  return (
    <div>
      <PageHead
        label="Find Jobs"
        title="Global matches, one place"
        sub="Onsite, remote and hybrid roles aggregated and matched to your Profile."
      />

      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center">
        {/* Search input */}
        <div className="flex flex-1 items-center gap-2 rounded-full border border-border bg-card px-4 shadow-xs">
          <Search className="h-4 w-4 text-muted-foreground shrink-0" />
          <input
            data-testid="job-search"
            value={q}
            onChange={(e) => handleQueryChange(e.target.value)}
            placeholder="Search role, company or keywords…"
            className="w-full bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground"
          />
          {loading && <Loader2 className="h-4 w-4 animate-spin text-electric shrink-0" />}
        </div>

        {/* Country Selector */}
        <div className="flex items-center gap-2 rounded-full border border-border bg-card px-3.5 py-1.5 shadow-xs shrink-0">
          <Globe className="h-4 w-4 text-electric shrink-0" />
          <select
            value={country}
            onChange={(e) => handleCountryChange(e.target.value)}
            data-testid="country-select"
            className="bg-transparent text-sm font-semibold outline-none cursor-pointer py-1.5 pr-2"
          >
            {COUNTRIES.map((c) => (
              <option key={c.code} value={c.code} className="bg-card text-foreground">
                {c.flag} {c.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-2">
          {FILTERS.map((f) => (
            <button
              key={f}
              onClick={() => handleFilterChange(f)}
              data-testid={`filter-${f.toLowerCase().replace(/\s/g, "-")}`}
              className={`rounded-full border px-4 py-2 text-sm font-semibold transition-colors ${
                filter === f ? "border-electric bg-electric text-white shadow-xs" : "border-border hover:bg-secondary"
              }`}
            >
              {f}
            </button>
          ))}
        </div>

        {totalJobs > 0 && (
          <p className="text-xs text-muted-foreground font-medium">
            Showing <strong className="text-foreground">{startIdx} - {endIdx}</strong> of <strong className="text-foreground">{totalJobs}</strong> jobs
          </p>
        )}
      </div>

      <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {jobList.map((j) => (
          <JobCard
            key={j.id}
            job={j}
            saved={saved[j.id]}
            onSave={() => {
              setSaved((s) => ({ ...s, [j.id]: !s[j.id] }));
              toast(saved[j.id] ? "Removed from saved" : `Saved ${j.role}`);
            }}
            onApply={async () => {
              try {
                await api.post("/applications", {
                  company: j.company,
                  role: j.role,
                  status: "Applied",
                  location: j.location,
                  salary: j.salary,
                  match_score: j.match,
                });
              } catch (_) {}

              const targetUrl = (j.apply_url && j.apply_url !== "#") ? j.apply_url : (j.link || j.url);
              if (targetUrl && targetUrl !== "#") {
                window.open(targetUrl, "_blank", "noopener,noreferrer");
                toast.success(`Opening job posting for ${j.role}`);
              } else {
                toast.success(`Applied for ${j.role} (Saved to Tracker)`);
                navigate("/app/tracker");
              }
            }}
          />
        ))}
        {jobList.length === 0 && !loading && (
          <div className="col-span-full py-12 text-center text-muted-foreground">
            <p className="font-heading text-base font-semibold">No jobs found matching your criteria</p>
            <p className="text-xs mt-1">Try broadening your search query or selecting "All Countries".</p>
          </div>
        )}
      </div>

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="mt-8 flex flex-col sm:flex-row items-center justify-between gap-4 border-t border-border pt-6">
          <p className="text-xs text-muted-foreground">
            Page <strong className="text-foreground">{page}</strong> of <strong className="text-foreground">{totalPages}</strong>
          </p>

          <div className="flex items-center gap-2">
            <button
              onClick={() => handlePageChange(page - 1)}
              disabled={page <= 1 || loading}
              className="flex items-center gap-1 rounded-xl border border-border bg-card px-3 py-2 text-xs font-semibold hover:bg-secondary disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronLeft className="h-4 w-4" /> Previous
            </button>

            <div className="flex items-center gap-1">
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter((p) => p === 1 || p === totalPages || Math.abs(p - page) <= 1)
                .map((p, idx, arr) => {
                  const prevP = arr[idx - 1];
                  const showEllipsis = prevP && p - prevP > 1;
                  return (
                    <React.Fragment key={p}>
                      {showEllipsis && <span className="px-1 text-xs text-muted-foreground">…</span>}
                      <button
                        onClick={() => handlePageChange(p)}
                        className={`h-8 w-8 rounded-lg text-xs font-bold transition-colors ${
                          p === page
                            ? "bg-electric text-white shadow-xs"
                            : "border border-border bg-card hover:bg-secondary text-foreground"
                        }`}
                      >
                        {p}
                      </button>
                    </React.Fragment>
                  );
                })}
            </div>

            <button
              onClick={() => handlePageChange(page + 1)}
              disabled={page >= totalPages || loading}
              className="flex items-center gap-1 rounded-xl border border-border bg-card px-3 py-2 text-xs font-semibold hover:bg-secondary disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              Next <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
